import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import '../stylesheets/PageSetPassword.css'; // Replaced by Tailwind
import { useTranslation } from "react-i18next";
import { getBaseUrl } from '../config/apiConfig';
import { ShieldCheck, Eye, EyeOff, AlertCircle, CheckCircle2, Key } from "lucide-react";

const PageSetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [status, setStatus] = useState(null);
  const [backendError, setBackendError] = useState('');
  const [showRequirements, setShowRequirements] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return {
      isValid: minLength && hasUpperCase && hasNumber && hasSpecialChar,
      minLength,
      hasUpperCase,
      hasNumber,
      hasSpecialChar
    };
  };

  const handleSetPassword = async () => {
    const validation = validatePassword(password1);
    if (!validation.isValid) {
      setStatus('invalid');
      return;
    }
    if (password1 !== password2) {
      setStatus('notmatch');
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    if (!token) {
      setStatus('notoken');
      return;
    }
    try {
      const response = await fetch(`${getBaseUrl()}/api/user/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: password1 }),
      });

      const data = await response.json();

      if (!response.ok) {
        setBackendError(data.error || data.message || t("setPassword.passwordSetError"));
        setStatus('error');
        return;
      }

      setStatus('success');
      setTimeout(() => navigate('/login'), 1500);
    } catch (error) {
      console.error(error);
      setBackendError(t("setPassword.networkError"));
      setStatus('error');
    }
  };

  const requirements = validatePassword(password1);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-500">

        {/* Header Section */}
        <div className="bg-[#0f7699] p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="h-full w-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]" />
          </div>
          <div className="flex flex-col items-center relative z-10">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-white backdrop-blur-md border border-white/20 mb-6 shadow-xl">
              <ShieldCheck size={32} strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold text-white tracking-tight">{t("setPassword")}</h2>
            <p className="text-blue-100/70 text-[11px] font-bold uppercase tracking-widest mt-2">{t("setPassword.createSecurePassword")}</p>
          </div>
        </div>

        {/* Form Section */}
        <div className="p-10 space-y-8">
          <div className="space-y-6">

            {/* New Password Input */}
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                {t("passwordPage.newPassword")}
              </label>
              <div className="relative group">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f7699] transition-colors" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  value={password1}
                  onChange={e => setPassword1(e.target.value)}
                  onFocus={() => setShowRequirements(true)}
                  className="w-full h-12 pl-12 pr-12 bg-slate-50 dark:bg-slate-700 border-2 border-slate-100 dark:border-slate-600 rounded-xl focus:border-[#0f769940] dark:focus:border-[#4fc3f7]/40 focus:bg-white dark:focus:bg-slate-600 transition-all text-slate-800 dark:text-white outline-none font-medium"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password Requirements HUD */}
            {showRequirements && (
              <div className="p-6 bg-slate-50 dark:bg-slate-700 rounded-2xl border border-slate-100 dark:border-slate-600 space-y-4 animate-in fade-in slide-in-from-top-2">
                <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("passwordPage.passwordMust")}</p>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { key: 'minLength', label: t("passwordPage.minLength") },
                    { key: 'hasUpperCase', label: t("passwordPage.upperCase") },
                    { key: 'hasNumber', label: t("passwordPage.number") },
                    { key: 'hasSpecialChar', label: t("passwordPage.specialChar") }
                  ].map(req => (
                    <div key={req.key} className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${requirements[req.key] ? 'bg-green-500' : 'bg-slate-200'}`}>
                        <CheckCircle2 size={10} className="text-white" />
                      </div>
                      <span className={`text-[11px] font-medium transition-colors ${requirements[req.key] ? 'text-green-700' : 'text-slate-400'}`}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Repeat Password Input */}
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                {t("passwordPage.repeatPassword")}
              </label>
              <div className="relative group">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f7699] transition-colors" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  value={password2}
                  onChange={e => setPassword2(e.target.value)}
                  className="w-full h-12 pl-12 pr-12 bg-slate-50 dark:bg-slate-700 border-2 border-slate-100 dark:border-slate-600 rounded-xl focus:border-[#0f769940] dark:focus:border-[#4fc3f7]/40 focus:bg-white dark:focus:bg-slate-600 transition-all text-slate-800 dark:text-white outline-none font-medium"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleSetPassword}
            disabled={!requirements.isValid || password1 !== password2}
            className="w-full h-14 bg-[#0f7699] hover:bg-[#0d6482] text-white rounded-2xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-blue-900/20 transition-all active:scale-[0.98] disabled:opacity-30 disabled:grayscale"
          >
            {t("passwordPage.setPasswordButton")}
          </button>

          {/* Status Messages */}
          {status && status !== 'success' && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex gap-4 animate-in fade-in slide-in-from-bottom-2">
              <AlertCircle size={20} className="text-red-500 shrink-0" />
              <div className="space-y-1">
                <h4 className="text-[11px] font-bold text-red-900 uppercase">{t("setPassword.errorTitle")}</h4>
                <p className="text-[11px] text-red-700 font-medium leading-relaxed">
                   {status === 'notmatch' && t("passwordPage.passwordsNoMatch")}
                  {status === 'invalid' && t("passwordPage.passwordRequirementsNotMet")}
                  {status === 'notoken' && t("passwordPage.noTokenFound")}
                  {status === 'error' && (backendError || t("passwordPage.passwordSetError"))}
                </p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="p-4 bg-green-50 border border-green-100 rounded-2xl flex gap-4 animate-in fade-in slide-in-from-bottom-2">
              <CheckCircle2 size={20} className="text-green-500 shrink-0" />
              <div className="space-y-1">
                <h4 className="text-[11px] font-bold text-green-900 uppercase">{t("setPassword.successTitle")}</h4>
                <p className="text-[11px] text-green-700 font-medium leading-relaxed">
                  {t("passwordPage.passwordSetSuccess")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageSetPassword;
